// React & Hooks
import { memo, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";

// Validation & Types
import { Errors } from "services/forms/validation/types";
import { FLEET_VEHICLE_FIRE_EXTINGUISHER_EDIT_FORM_SCHEMA } from "./validation";
import { VehicleFireExtinguisherEditProps } from "./types";
import { FleetVehicleFireExtinguisherEditFormValues } from "services/api/fleet/vehicles/fireextinguishers/types";
import { initialFleetVehicleFireExtinguisherEditForm } from "services/api/fleet/vehicles/fireextinguishers/consts";

// API
import {
  createFleetVehicleFireExtinguisherAPI,
  getFleetVehicleFireExtinguisherAPI,
  updateFleetVehicleFireExtinguisherAPI,
} from "services/api/fleet/vehicles/fireextinguishers/api";

// Form Hooks
import {
  useHandleDateChange,
  useHandleNumberFieldChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { DatePicker } from "ui/components/DatePicker";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import { NumberField } from "ui/components/TextField/NumberField";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import {
  FLEET_VEHICLE_FIRE_EXTINGUISHER_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_FIRE_EXTINGUISHER_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

const VehicleFireExtinguisherEditCard: React.FC<
  VehicleFireExtinguisherEditProps
> = (props) => {
  const { show, vehicleId, fireExtinguisherId, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);



  const [formValues, setFormValues] =
    useState<FleetVehicleFireExtinguisherEditFormValues>(
      initialFleetVehicleFireExtinguisherEditForm
    );

  useEffect(() => {
    setFormValues(initialFleetVehicleFireExtinguisherEditForm);
    if (fireExtinguisherId) {
      fetchFireExtinguisher(fireExtinguisherId);
    }
  }, [fireExtinguisherId]);

  const fetchFireExtinguisher = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleFireExtinguisherEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleFireExtinguisherAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_FIRE_EXTINGUISHER_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!vehicleId && !!fireExtinguisherId) {
        result = await updateFleetVehicleFireExtinguisherAPI(
          vehicleId,
          fireExtinguisherId,
          formValues
        );
      } else {
        result = await createFleetVehicleFireExtinguisherAPI(
          vehicleId,
          formValues
        );
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleNumberFieldChange = useHandleNumberFieldChange(
    setFormValues,
    formValues
  );

  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose
    })
  }

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={fireExtinguisherId
          ? FLEET_VEHICLE_FIRE_EXTINGUISHER_EDIT_PAGE_TITLE.text
          : FLEET_VEHICLE_FIRE_EXTINGUISHER_CREATE_PAGE_TITLE.text
        }
        onHide={onClose}
      />
      <ModalBody overflow={false}>
        <div className="flex gap-4">
          <div className="w-full  m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="flex flex-col gap-3 mb-3 ">
                  <div className="flex gap-3 ">
                    <div className="flex-1 ">
                      <TextField
                        type="text"
                        name="name"
                        label={t(
                          "admin.fleet.vehicle.fireextinguisher.add.name"
                        )}
                        placeholder={t(
                          "admin.fleet.vehicle.fireextinguisher.add.name.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.name}
                        value={formValues.name}
                        required
                        endIcon=""
                      ></TextField>
                    </div>

                  </div>
                  <div className="flex gap-3 ">
                    <div className="flex-1 ">
                      <NumberField
                        name="price"
                        label={t(
                          "admin.fleet.vehicle.fireextinguisher.add.price"
                        )}
                        placeholder={t(
                          "admin.fleet.vehicle.fireextinguisher.add.price.placeholder"
                        )}
                        onChange={handleNumberFieldChange}
                        error={errors.price}
                        value={formValues.price}
                        required
                        endIcon=""
                        decimals={2}
                        groupAppend="€"
                      ></NumberField>
                    </div>
                    <div className="flex-1 ">
                      <DatePicker
                        name="expirationDate"
                        label={t(
                          "admin.fleet.vehicle.fireextinguisher.add.expirationdate"
                        )}
                        placeholder={t(
                          "admin.fleet.vehicle.fireextinguisher.add.expirationdate.placeholder"
                        )}
                        onChange={handleDateChange}
                        error={errors.expirationDate}
                        value={formValues.expirationDate}
                        width="full"
                        required
                      />
                    </div>

                  </div>
                  <div className="flex gap-3 ">
                    <div className="flex-1 ">
                      <NumberField

                        name="weight"
                        label={t(
                          "admin.fleet.vehicle.fireextinguisher.add.weight"
                        )}
                        placeholder={t(
                          "admin.fleet.vehicle.fireextinguisher.add.weight.placeholder"
                        )}
                        onChange={handleNumberFieldChange}
                        error={errors.weight}
                        value={formValues.weight}
                        required
                        endIcon=""
                        groupAppend="kg"
                      ></NumberField>
                    </div>
                    <div className="flex-1 ">
                      <TextField
                        type="text"
                        name="serialNumber"
                        label={t(
                          "admin.fleet.vehicle.fireextinguisher.add.serialnumber"
                        )}
                        placeholder={t(
                          "admin.fleet.vehicle.fireextinguisher.add.serialnumber.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.serialNumber}
                        value={formValues.serialNumber}
                        required
                        endIcon=""
                      ></TextField>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={handleCloseModal}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    onClick={handleFormSubmit}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};
export default memo(VehicleFireExtinguisherEditCard);
